import { action, computed, observable, makeAutoObservable } from 'mobx';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FooterLegalTextState } from 'src/main/cms/FooterManagement/FooterManagement';
import { validateExpectLink, validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';

type FormType = {
    description_text: null | undefined | string;
    licence_number: string;
    licence_url: null | undefined | string;
};

export class FooterLegalTextDataState {
    @observable public descriptionText: FormInputState<string, string>;
    @observable public licenceNumber: FormInputState<string, string>;
    @observable public licenceUrl: FormInputState<string, string>;

    @observable public initialDescriptionText: string;
    @observable public initialLicenceNumber: string;
    @observable public initialLicenceUrl: string;

    private readonly form: FormModel<FormType>;

    private readonly noEmptyValidationMessage = 'This field is required.';
    private readonly incorrectLinkValidationMessage = 'Link should begin with http:// or https:/';
    private readonly repeatedLicenceNumberMessage =
        'Licence number has to be included in the description using the Insert button and can be only inserted once.';

    constructor(
        private readonly trpcClient: TrpcClient,
        private readonly configState: ConfigState,
        private readonly footerDataState: FooterLegalTextState
    ) {
        const defaultFooterText = this.configState.config.footerContentDefault;
        const footerDetailsData = footerDataState.footerLegalTextDetailsData ?? defaultFooterText;

        this.descriptionText = FormInputState.new(footerDetailsData.description_text ?? '')
            .map(validateNotEmpty(this.noEmptyValidationMessage))
            .map(this.validateLicenceNumberRepeat(this.repeatedLicenceNumberMessage));
        this.licenceNumber = FormInputState.new(footerDetailsData.licence_number ?? '').map(
            validateNotEmpty(this.noEmptyValidationMessage)
        );
        this.licenceUrl = FormInputState.new(footerDetailsData.licence_url ?? '').map(
            validateExpectLink(this.incorrectLinkValidationMessage)
        );

        this.initialDescriptionText = footerDetailsData.description_text ?? '';
        this.initialLicenceNumber = footerDetailsData.licence_number ?? '';
        this.initialLicenceUrl = footerDetailsData.licence_url ?? '';

        this.form = FormModel.group({
            description_text: this.descriptionText,
            licence_number: this.licenceNumber,
            licence_url: this.licenceUrl,
        });
        makeAutoObservable(this);
    }

    @action public insertLicenceNumber = (): void => {
        this.descriptionText.setValue(`${this.descriptionText.value}{{licenceNumber}}`);
    };

    @action public removeLicenceNumberLink = (): void => {
        this.licenceUrl.setValue('');
    };

    @computed public get disableLicenceNumberInsert(): boolean {
        if (this.descriptionText.value.includes('{{licenceNumber}}')) {
            return true;
        }
        return false;
    }

    @action public resetToDefaultFooterText = (): void => {
        const footerDetailsData = this.configState.config.footerContentDefault;

        this.form.reset();
        this.descriptionText.setValue(footerDetailsData.description_text);
        this.licenceNumber.setValue(footerDetailsData.licence_number);
        this.licenceUrl.setValue(footerDetailsData.licence_url);
    };

    @computed public get footerId(): number | null {
        return this.footerDataState.footerLegalTextDetailsData?.id ?? null;
    }

    @computed public get isFooterCreated(): boolean {
        return this.footerId !== null;
    }

    @action public handleCreateAndEdit = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;

        if (result.value.type === 'ok') {
            const footerDetailsData = {
                ...result.value.data,
                has_cookies: true,
            };

            if (this.footerId === null) {
                await this.trpcClient.client.footerTextRouter.createFooterText.mutate({
                    data: footerDetailsData,
                });
            } else {
                await this.trpcClient.client.footerTextRouter.updateFooterText.mutate({
                    data: footerDetailsData,
                    id: this.footerId,
                });
            }

            await this.footerDataState.refreshLegalTextData();
            this.initialDescriptionText = footerDetailsData.description_text ?? '';
            this.initialLicenceNumber = footerDetailsData.licence_number ?? '';
            this.initialLicenceUrl = footerDetailsData.licence_url ?? '';
        }
    };
    @action private validateLicenceNumberRepeat =
        (message: string) =>
        (value: string): Result<string> => {
            const descriptionTextArray = this.descriptionText.value.split(' ');
            const licenceNumberCount = descriptionTextArray.filter((item) => item.includes('{{licenceNumber}}'));

            if (licenceNumberCount.length === 0) {
                return Result.createError(message);
            }

            if (licenceNumberCount.length > 1) {
                return Result.createError(message);
            }

            return Result.createOk(value);
        };

    @computed public get hasInputChanged(): boolean {
        const hasDescriptionChanged = this.initialDescriptionText !== this.descriptionText.value;
        const hasLicenceNumberChanged = this.initialLicenceNumber !== this.licenceNumber.value;
        const hasLicenceUrlChanged = this.initialLicenceUrl !== this.licenceUrl.value;

        if (!hasDescriptionChanged && !hasLicenceNumberChanged && !hasLicenceUrlChanged) {
            return false;
        }

        return true;
    }
}
